/// <amd-module name="Repos/Views/CreatePullRequest/CreatePullRequestViewService" />
import { translateDisplayNamesToStorageKeys } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { useStorageKeysInMentions } from "Mention/Common/Util";
import { getChangesCollection } from "Repos/Changes/Util";
import { getGitNavigationService, IGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import {
    GitForkRef,
    GitPullRequest,
    GitQueryCommitsCriteria,
    GitRepository,
    GitRepositoryRef,
    GitVersionDescriptor,
    IdentityRefWithVote,
    VersionControlChangeType
} from "Repos/Common/Contracts/Git";
import { ICommit } from "Repos/Common/Contracts/History";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getShortCommitComment } from "Repos/Common/Util/Commit";
import { getFileName } from "Repos/Common/Util/File";
import { branchNameToRef, getShortCommitId, refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor, commitToVersionDescriptor, getVersionString } from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { getReposMessagesService, IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { calculateTrueContentLength, tryUpdateBlobs } from "Repos/PullRequest/Attachments/Attachment";
import { getSuggestedTagsService } from "Repos/PullRequestCommon/SuggestedTagsService";
import { maxDescriptionLength } from "Repos/PullRequestCommon/PullRequestDescriptionService";
import * as Resources from "Repos/Views/CreatePullRequest/Resources";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { IUnsavedChangesService } from "VSS/Features/PlatformUI/UnsavedChangesService";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";
import { traceException } from "VSS/Platform/Trace";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { getGitClient } from "./RestClient/Git";

/**
 * Information about the source and target branch to create a pull request from/to
 */
export interface IPullRequestSourceTargetInfo {
    repositories: GitRepository[];
    srcBranch: string;
    srcRepository: GitRepository;
    tgtBranch: string;
    tgtRepository: GitRepository;
}

/**
 * Creation-related information pertaining to the selected source/target branches
 */
export interface IPullRequestCreateInfo {
    activePullRequestId?: number;
    commits: ICommit[];
    changes: IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor> | undefined>;
    draftIsDefault: boolean;
    sourceVersionString: string;
    targetVersionString: string;
    sourceTargetInfo: IPullRequestSourceTargetInfo;
    templates: IPullRequestTemplate[];
    maxNumberOfCommits: number;
}

export interface IPullRequestTemplate {
    isDefault: boolean;
    name: string;
    path: string;
}

export interface ICreatePullRequestViewService extends IVssService {
    addCommitMessagesToDescription(): void;
    addOptionalReviewer(identity: IIdentity): void;
    addRequiredReviewer(identity: IIdentity): void;
    addTag(tagName: string): void;
    addTemplateToDescription(templatePath: string): Promise<void>;
    createPullRequest(draft?: boolean): Promise<GitPullRequest>;
    getCreateInfo(): IReadonlyObservableValue<IPullRequestCreateInfo | undefined>;
    getDescription(): IReadonlyObservableValue<string>;
    getOptionalReviewers(): IReadonlyObservableArray<IIdentity>;
    getPullRequestCreationId(): number;
    getRequiredReviewers(): IReadonlyObservableArray<IIdentity>;
    getSourceTargetInfo(): IReadonlyObservableValue<IPullRequestSourceTargetInfo>;
    getTags(): IReadonlyObservableArray<string>;
    getTitle(): IReadonlyObservableValue<string>;
    removeOptionalReviewers(identities: IIdentity[]): void;
    removeRequiredReviewers(identities: IIdentity[]): void;
    removeTag(tagName: string): void;
    resolveReviewerEmail(email: string, item: IIdentity | null): void;
    setDescription(description: string): void;
    setSourceTargetInfo(info: Partial<IPullRequestSourceTargetInfo>): void;
    setTitle(title: string): void;
}

const createPRDataProviderId = "ms.vss-code-web.pr-create-data-provider";
interface ICreatePullRequestDetailsData {
    activePullRequestId?: number;
    defaultTemplatePath?: string;
    description?: string;
    draftIsDefault: boolean;
    sourceRef?: string;
    sourceRepositoryForkChildren?: GitRepository[];
    sourceRepositoryForkParent?: GitRepositoryRef;
    targetRef?: string;
    template?: string;
    templateList?: string[];
    templateName?: string;
    title?: string;
    maxNumberOfCommits: number;
}

const diffCommitsDataProviderId = "ms.vss-code-web.diff-commits-data-provider";
interface IDiffCommitsData {
    allChangesIncluded: boolean;
    changeCounts: { [changeType: number]: number };
    changes: IDiffCommitChange[];
    commitId: { full: string; short: string };
}
interface IDiffCommitChange {
    changeType: VersionControlChangeType;
    sourceServerItem: string;
    item: {
        serverItem: string;
        isFolder: boolean;
        isSymLink: boolean;
    };
}

class CreatePullRequestViewService extends VssService implements ICreatePullRequestViewService {
    private createInfo = new ObservableValue<IPullRequestCreateInfo | undefined>(undefined);
    private createdPullRequest = false;
    private defaultDescription = "";
    private defaultTitle = "";
    private description = new ObservableValue<string>("");
    private maxNumberOfChanges = 1000;
    private optionalReviewers = new ObservableArray<IIdentity>();
    private pullRequestCreationId = 0;
    private requiredReviewers = new ObservableArray<IIdentity>();
    private sourceTargetInfo: ObservableValue<IPullRequestSourceTargetInfo>;
    private tags = new ObservableArray<string>();
    private templatesByPath: { [templatePath: string]: string } = {};
    private title = new ObservableValue("");
    /**
     * Maximum number of commits shown in the create pull request preview
     */
    private maxNumberOfCommits: number;
    private defaultMaxNumberOfCommits = 100;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<ICreatePullRequestDetailsData>(createPRDataProviderId);
        this.maxNumberOfCommits = data?.maxNumberOfCommits ?? this.defaultMaxNumberOfCommits;


        const vcViewModelService = pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const repository = vcViewModelService.getRepository()!;

        const sourceTargetInfo = {
            repositories: (data && data.sourceRepositoryForkChildren) || [],
            srcBranch: (data && data.sourceRef) || "",
            srcRepository: repository,
            tgtBranch: (data && data.targetRef) || "",
            tgtRepository: repository
        };

        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();

        const srcRepositoryId = historyState["sourceRepositoryId"] || repository.id;
        const tgtRepositoryId =
            historyState["targetRepositoryId"] || (data && data.sourceRepositoryForkParent && data.sourceRepositoryForkParent.id) || repository.id;

        if (data && data.sourceRepositoryForkChildren) {
            const srcRepository = data.sourceRepositoryForkChildren.filter(r => r.id === srcRepositoryId)[0];
            if (srcRepository) {
                sourceTargetInfo.srcRepository = srcRepository;
            }
            const tgtRepository = data.sourceRepositoryForkChildren.filter(r => r.id === tgtRepositoryId)[0];
            if (tgtRepository) {
                sourceTargetInfo.tgtRepository = tgtRepository;
            }
        }

        if (!sourceTargetInfo.tgtBranch) {
            sourceTargetInfo.tgtBranch = refToBranchName(sourceTargetInfo.tgtRepository.defaultBranch);

            if (!sourceTargetInfo.tgtBranch && repository.id === tgtRepositoryId) {
                sourceTargetInfo.tgtBranch = refToBranchName(repository.defaultBranch);
            }
        }
        if (!sourceTargetInfo.srcBranch) {
            const defaultUserBranch = vcViewModelService.getDefaultGitBranchName();
            if (defaultUserBranch && defaultUserBranch !== sourceTargetInfo.tgtBranch) {
                sourceTargetInfo.srcBranch = defaultUserBranch;
            }
        }

        this.sourceTargetInfo = new ObservableValue(sourceTargetInfo);

        this.pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService").register({
            isDirty: () => {
                return !this.createdPullRequest && (this.description.value !== this.defaultDescription || this.title.value !== this.defaultTitle);
            }
        });

        getSuggestedTagsService(this.pageContext).setUsedTags(this.tags);

        this.handleSourceTargetChanged(data, historyState["cherryPickCommit"], historyState["revertCommit"]);
    }

    public addCommitMessagesToDescription(): void {
        if (this.createInfo.value && this.createInfo.value.commits.length > 0) {
            this.appendToDescription(
                this.createInfo.value.commits
                    .map(c => "- " + getShortCommitComment(c.comment, 250))
                    .reverse()
                    .join("\n")
            );
        }
    }

    public addOptionalReviewer(identity: IIdentity): void {
        this.optionalReviewers.push(identity);
    }

    public addRequiredReviewer(identity: IIdentity): void {
        this.requiredReviewers.push(identity);
    }

    public addTag(tagName: string): void {
        this.tags.push(tagName);
    }

    public async addTemplateToDescription(templatePath: string): Promise<void> {
        const srcTgt = this.sourceTargetInfo.value;
        if (srcTgt) {
            let templateText = this.templatesByPath[templatePath];
            if (templateText === undefined) {
                templateText = await getGitClient(this.pageContext).getItemText(
                    srcTgt.tgtRepository.id,
                    templatePath,
                    srcTgt.tgtRepository.project.id
                );
            }

            if (templateText) {
                this.appendToDescription(templateText);
            }
        }
    }

    public async createPullRequest(draft?: boolean): Promise<GitPullRequest> {
        const srcTgt = this.sourceTargetInfo.value;
        let forkSource: GitForkRef | undefined;
        if (srcTgt.srcRepository.id !== srcTgt.tgtRepository.id) {
            forkSource = {
                repository: srcTgt.srcRepository
            } as GitForkRef;
        }

        const description = useStorageKeysInMentions(this.pageContext)
            ? translateDisplayNamesToStorageKeys(this.pageContext, this.description.value)
            : this.description.value;

        if (calculateTrueContentLength(this.pageContext, description) > maxDescriptionLength) {
            throw new Error(Resources.DescriptionTooLongError);
        }

        const prToCreate: GitPullRequest = {
            description,
            isDraft: draft,
            forkSource,
            labels: this.tags.value.map(x => {
                return { name: x };
            }),
            reviewers: [
                ...this.requiredReviewers.value.map(r => this.identityToVoter(r, true)),
                ...this.optionalReviewers.value
                    .filter(or => !this.requiredReviewers.value.some(rr => rr.subjectDescriptor === or.subjectDescriptor))
                    .map(r => this.identityToVoter(r, false))
            ] as IdentityRefWithVote[],
            sourceRefName: branchNameToRef(srcTgt.srcBranch),
            targetRefName: branchNameToRef(srcTgt.tgtBranch),
            title: this.title.value
        } as GitPullRequest;

        let pullRequest = await getGitClient(this.pageContext).createPullRequest(
            prToCreate,
            srcTgt.tgtRepository.id,
            srcTgt.tgtRepository.project.id,
            true
        );
        this.createdPullRequest = true;

        const pullRequestMarkdownContext = {
            projectId: srcTgt.tgtRepository.project.id,
            pullRequestId: pullRequest.pullRequestId,
            repositoryId: srcTgt.tgtRepository.id
        };

        try {
            const updatedDescription = await tryUpdateBlobs(this.pageContext, pullRequestMarkdownContext, pullRequest.description);
            pullRequest = await getGitClient(this.pageContext).updatePullRequest(
                { description: updatedDescription } as GitPullRequest,
                pullRequest.repository.id,
                pullRequest.pullRequestId,
                pullRequest.repository.project.id
            );
        } catch (ex) {
            // Show error in ReposMessages
            const repoMessagesService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
            repoMessagesService.showMessage({ renderMessage: () => ex.message, severity: MessageCardSeverity.Warning }, true);
        }

        return pullRequest;
    }

    public getCreateInfo(): IReadonlyObservableValue<IPullRequestCreateInfo | undefined> {
        return this.createInfo;
    }

    public getDescription(): IReadonlyObservableValue<string> {
        return this.description;
    }

    public getOptionalReviewers(): IReadonlyObservableArray<IIdentity> {
        return this.optionalReviewers;
    }

    public getPullRequestCreationId(): number {
        return this.pullRequestCreationId;
    }

    public getRequiredReviewers(): IReadonlyObservableArray<IIdentity> {
        return this.requiredReviewers;
    }

    public getSourceTargetInfo(): IReadonlyObservableValue<IPullRequestSourceTargetInfo> {
        return this.sourceTargetInfo;
    }

    public getTags(): IReadonlyObservableArray<string> {
        return this.tags;
    }

    public getTitle(): IReadonlyObservableValue<string> {
        return this.title;
    }

    public removeOptionalReviewers(identities: IIdentity[]): void {
        this.optionalReviewers.value = this.optionalReviewers.value.filter(
            reviewer => identities.filter(i => i.entityId === reviewer.entityId).length === 0
        );
    }

    public removeRequiredReviewers(identities: IIdentity[]): void {
        this.requiredReviewers.value = this.requiredReviewers.value.filter(
            reviewer => identities.filter(i => i.entityId === reviewer.entityId).length === 0
        );
    }

    public removeTag(tagName: string): void {
        tagName = tagName.toLowerCase();
        this.tags.removeAll(x => x.toLowerCase() === tagName);
    }

    public resolveReviewerEmail(email: string, item: IIdentity | null): void {
        if (item) {
            const optionalIndex = this.optionalReviewers.value.findIndex(identity => identity.entityId === email);
            if (optionalIndex >= 0) {
                this.optionalReviewers.change(optionalIndex, item);
            }

            const requiredIndex = this.requiredReviewers.value.findIndex(identity => identity.entityId === email);
            if (requiredIndex >= 0) {
                this.requiredReviewers.change(requiredIndex, item);
            }
        } else {
            this.optionalReviewers.removeAll(identity => identity.entityId === email);
            this.requiredReviewers.removeAll(identity => identity.entityId === email);
        }
    }

    public setDescription(description: string): void {
        this.description.value = description;
    }

    public setSourceTargetInfo(info: Partial<IPullRequestSourceTargetInfo>): void {
        this.sourceTargetInfo.value = {
            ...this.sourceTargetInfo.value,
            ...info
        };

        this.handleSourceTargetChanged();
    }

    public setTitle(title: string): void {
        this.title.value = title;
    }

    private appendToDescription(value: string): void {
        let newDescription = this.description.value;
        if (newDescription) {
            newDescription += "\n";
        }
        newDescription += value;
        this.description.value = newDescription;
    }

    private getSourceTargetQueryParams(srcTgt: IPullRequestSourceTargetInfo) {
        return {
            sourceRef: srcTgt.srcBranch,
            targetRef: srcTgt.tgtBranch,
            sourceRepositoryId: srcTgt.srcRepository.id,
            targetRepositoryId: srcTgt.tgtRepository.id
        };
    }

    private identityToVoter(identity: IIdentity, isRequired: boolean): IdentityRefWithVote {
        const isAad = identity.originDirectory == "aad" && !identity.localId;
        return {
            descriptor: identity.subjectDescriptor,
            displayName: identity.displayName,
            id: isAad ? identity.originId : identity.localId,
            isAadIdentity: isAad,
            isRequired: isRequired,
            isContainer: equals(identity.entityType, "group", true)
        } as IdentityRefWithVote;
    }

    private async handleSourceTargetChanged(dataProviderData?: ICreatePullRequestDetailsData, cherryPickCommit?: string, revertCommit?: string) {
        this.createInfo.value = undefined;
        const srcTgt = this.sourceTargetInfo.value;

        // Reset the PR creation id when the source or target branch has changed
        const pullRequestCreationId = ++this.pullRequestCreationId;

        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        historyService.replaceState({ ...historyService.getState(), ...this.getSourceTargetQueryParams(srcTgt) });

        if (!srcTgt.srcBranch || !srcTgt.tgtBranch) {
            return;
        }

        if (!dataProviderData) {
            try {
                const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                dataProviderData = await contributionService.getDataAsync<ICreatePullRequestDetailsData>(
                    createPRDataProviderId,
                    this.getSourceTargetQueryParams(srcTgt),
                    true
                );
            } catch (error) {
                this.showError(error);
                return;
            }
        }

        const providerData = dataProviderData!;

        const templates: IPullRequestTemplate[] = [];
        if (providerData.defaultTemplatePath) {
            this.templatesByPath[providerData.defaultTemplatePath] = providerData.template || "";
            templates.push({
                isDefault: true,
                name: getFileName(providerData.defaultTemplatePath),
                path: providerData.defaultTemplatePath
            });
        }
        if (providerData.templateList) {
            providerData.templateList.sort(localeIgnoreCaseComparer);
            templates.push(
                ...providerData.templateList.map(t => {
                    return {
                        isDefault: false,
                        name: getFileName(t),
                        path: t
                    };
                })
            );
        }

        const gitClient = getGitClient(this.pageContext);
        let srcVersion: GitVersionDescriptor | undefined;
        let tgtVersion: GitVersionDescriptor | undefined;
        if (srcTgt.srcRepository.id !== srcTgt.tgtRepository.id) {
            try {
                // Get commit id for source and target branches
                const tgtCommit = (await gitClient.getRefs(srcTgt.tgtRepository.id, srcTgt.tgtRepository.project.id, "heads/" + srcTgt.tgtBranch))[0];
                const srcCommit = (await gitClient.getRefs(srcTgt.srcRepository.id, srcTgt.srcRepository.project.id, "heads/" + srcTgt.srcBranch))[0];

                const mergeBase = (
                    await gitClient.getMergeBases(
                        srcTgt.tgtRepository.id,
                        tgtCommit.objectId,
                        srcCommit.objectId,
                        srcTgt.tgtRepository.project.id,
                        undefined,
                        srcTgt.srcRepository.id
                    )
                )[0];

                srcVersion = commitToVersionDescriptor(srcCommit.objectId);
                tgtVersion = commitToVersionDescriptor(mergeBase.commitId);
            } catch (ex) {
                console.error(`FindMergeBaseFailed: ${ex.message}`);
                traceException(this.pageContext, "Repos", "CreatePullRequestService", ex, { context: "FindMergeBase" });
            }
        } else {
            srcVersion = branchToVersionDescriptor(srcTgt.srcBranch);
            tgtVersion = branchToVersionDescriptor(srcTgt.tgtBranch);
        }

        let commits: ICommit[] = [];
        let newTitle = providerData.title || "";
        let newDescription = providerData.description || "";

        if (srcVersion && tgtVersion) {
            try {
                const commitResults = await gitClient.getCommitsBatch(
                    {
                        $top: this.maxNumberOfCommits,
                        compareVersion: srcVersion,
                        itemVersion: tgtVersion
                    } as GitQueryCommitsCriteria,
                    srcTgt.srcRepository.id,
                    srcTgt.srcRepository.project.id
                );

                const routingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
                commits = commitResults.map(c => {
                    return {
                        comment: c.comment,
                        commit: c,
                        linkProps: {
                            href: routingService.getCommitUrl(
                                srcTgt.srcRepository.name,
                                srcTgt.srcRepository.project.name,
                                c.commitId,
                                branchNameToRef(srcTgt.srcBranch)
                            )
                        },
                        repositoryId: srcTgt.srcRepository.id,
                        shortCommitId: getShortCommitId(c.commitId)
                    };
                });

                if (commitResults.length === 1) {
                    if (!newTitle) {
                        newTitle = getShortCommitComment(commitResults[0].comment);
                    }
                    if (!newDescription) {
                        if (commitResults[0].commentTruncated) {
                            newDescription = (
                                await gitClient.getCommit(commitResults[0].commitId, srcTgt.srcRepository.id, srcTgt.srcRepository.project.id, 0)
                            ).comment;
                        } else {
                            newDescription = commitResults[0].comment;
                        }
                    }
                }

                if (providerData.template) {
                    newDescription = newDescription ? `${providerData.template}\n\n${newDescription}` : providerData.template;
                }
            } catch (error) {
                this.showError(error);
                return;
            }
        }

        if (cherryPickCommit) {
            const line = format(Resources.CherryPickedFromCommit, getShortCommitId(cherryPickCommit));
            newDescription = newDescription ? `${newDescription}\n\n${line}` : line;
        } else if (revertCommit) {
            const line = format(Resources.RevertedCommit, getShortCommitId(revertCommit));
            newDescription = newDescription ? `${newDescription}\n\n${line}` : line;
        }

        // Make sure that the user did not change branches since we started looking up this information
        if (this.pullRequestCreationId === pullRequestCreationId) {
            if (newTitle !== undefined) {
                // Update the title if the user hasn't already edited it
                if (!this.title.value || this.title.value === this.defaultTitle) {
                    this.title.value = newTitle;
                }

                this.defaultTitle = newTitle;
            }

            if (newDescription !== undefined) {
                // Update the description if the user hasn't already edited it
                if (!this.description.value || this.description.value === this.defaultDescription) {
                    this.description.value = newDescription;
                } else if (providerData.template) {
                    this.description.value += "\n" + newDescription;
                }

                this.defaultDescription = newDescription;
            }

            const changes = new ObservableValue<IChangeCollection<GitVersionDescriptor> | undefined>(undefined);

            const navigationService: IGitNavigationService = getGitNavigationService(this.pageContext);
            navigationService.setChanges(changes);

            this.createInfo.value = {
                activePullRequestId: providerData.activePullRequestId,
                commits,
                changes,
                sourceTargetInfo: srcTgt,
                sourceVersionString: srcVersion ? getVersionString(srcVersion) : "",
                targetVersionString: tgtVersion ? getVersionString(tgtVersion) : "",
                templates,
                draftIsDefault: providerData.draftIsDefault,
                maxNumberOfCommits: providerData.maxNumberOfCommits,
            };

            if (srcVersion && tgtVersion && commits[0]?.commit?.commitId) {
                this.fetchChanges(srcVersion, tgtVersion, srcTgt, commits[0].commit.commitId, changes);
            }
        }
    }

    private fetchChanges(
        srcVersion: GitVersionDescriptor,
        tgtVersion: GitVersionDescriptor,
        srcTgt: IPullRequestSourceTargetInfo,
        sourceCommitId: string,
        changes: ObservableValue<IChangeCollection<GitVersionDescriptor> | undefined>
    ): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        contributionService
            .getDataAsync<IDiffCommitsData>(diffCommitsDataProviderId, {
                baseVersion: getVersionString(tgtVersion),
                maxNumberOfChanges: this.maxNumberOfChanges,
                repositoryId: srcTgt.tgtRepository.id,
                skipCount: 0,
                targetVersion: getVersionString(srcVersion)
            })
            .then(
                data => {
                    if (data) {
                        const targetVersion = data.commitId ? commitToVersionDescriptor(data.commitId.full) : tgtVersion;
                        changes.value = getChangesCollection<GitVersionDescriptor>(
                            data.changes.map(c => {
                                return {
                                    changeType: c.changeType,
                                    commitId: sourceCommitId,
                                    item: {
                                        isFolder: c.item.isFolder,
                                        isSymLink: c.item.isSymLink,
                                        originalPath: c.sourceServerItem,
                                        path: c.item.serverItem
                                    }
                                };
                            }),
                            srcTgt.tgtRepository.name,
                            sourceCommitId,
                            commitToVersionDescriptor(sourceCommitId),
                            targetVersion,
                            data.allChangesIncluded
                                ? undefined
                                : () => {
                                      this.maxNumberOfChanges = this.maxNumberOfChanges * 2;
                                      this.fetchChanges(srcVersion, tgtVersion, srcTgt, sourceCommitId, changes);
                                  }
                        );
                    }
                },
                error => {
                    this.showError(error);
                }
            );
    }

    private showError(error: Error): void {
        const repoMessagesService = getReposMessagesService(this.pageContext);
        repoMessagesService.showMessage({ renderMessage: () => error.message, severity: MessageCardSeverity.Error });
    }
}

Services.add("ICreatePullRequestViewService", { serviceFactory: CreatePullRequestViewService });
